import { useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { MemberGroups } from '../../../CMS/types.js'
import { useQueryParam } from '../../../common/hooks/useQueryParam.js'
import { T } from '../../../i18n/components/T.js'
import { NewChatFormFields } from '../../types.js'

import { NewChatMemberPicker } from './NewChatMemberPicker.js'
import { NewDirectChat } from './NewDirectChat.js'
import { NewGroupChat } from './NewGroupChat.js'

interface Props {
  canCreateGroup: boolean
  reachableMemberGroup: MemberGroups
  isCreatingGroup: boolean
}

export const NewChatForm = ({
  canCreateGroup,
  reachableMemberGroup,
  isCreatingGroup,
}: Props) => {
  const { control } = useFormContext()
  const members = useWatch({
    control,
    name: 'members',
  }) as NewChatFormFields['members']
  const [mode] = useQueryParam<'group'>({ name: 'mode' })

  const membersArray = useMemo(
    () => (Array.isArray(members) ? members : [members]),
    [members],
  )
  const isNewGroupChat = membersArray.length > 1 || mode === 'group'

  return (
    <div className="grid grid-rows-[auto_1fr] h-full overflow-hidden">
      <div className="p-3 pb-0 flex flex-col gap-y-3">
        <div className="grid grid-cols-[1fr_auto] items-center">
          <div
            className="font-medium text-lg truncate text-content leading-10"
            translate="no"
          >
            {isNewGroupChat ? (
              <T defaultMessage="New group" id="Generics.NewGroup" />
            ) : (
              <T defaultMessage="New message" id="Generics.NewMessage" />
            )}
          </div>
          <IconButton
            variant="tertiaryNeutral"
            icon={<SvgIcon name="close" />}
            as={Link}
            to="/messaging"
          />
        </div>
        <NewChatMemberPicker
          canCreateGroup={canCreateGroup}
          reachableMemberGroup={reachableMemberGroup}
        />
      </div>
      {isNewGroupChat && canCreateGroup ? (
        <NewGroupChat isCreatingGroup={isCreatingGroup} />
      ) : (
        <NewDirectChat memberId={membersArray.at(0)?.id} />
      )}
    </div>
  )
}
